'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDocumentInfo } from '@payloadcms/ui'

import { humanDuration, parseJobLog, type JobLogLevel } from '../../lib/jobLog'
import { errMessage, errName } from '../../lib/errors'

/**
 * Pannello live del singolo job — ed è la SOLA finestra che un autore ha sulla generazione.
 *
 * Prima mostrava il log grezzo così com'è in colonna: timestamp ISO in UTC (due ore indietro
 * rispetto all'orologio di chi guarda, in estate), stato in inglese e, in caso di guasto, una
 * riga tecnica del tipo «article-generator URL / OpenAI key non risolti per il tenant». Da lì un
 * autore non ricava nessuna delle tre cose che gli servono: se ha sbagliato lui, se deve chiamare
 * qualcuno, o se deve solo aspettare.
 *
 * Ora: ora locale, stato in italiano, livelli distinguibili a colpo d'occhio, il tempo speso da
 * ogni fase (che è come si vede quale passo si è impantanato) e, quando qualcosa va storto, la
 * diagnosi in cima con un bottone che la copia già formattata per i dev. Il testo della diagnosi
 * lo produce il worker (src/lib/jobDiagnosis.ts): qui si presenta soltanto.
 */

type JobSnapshot = {
  status?: 'queued' | 'running' | 'done' | 'error'
  progress?: number | null
  progressStage?: string | null
  jobLog?: string | null
  error?: string | null
  attempts?: number | null
  updatedAt?: string | null
}

const POLL_MS = 2_000
const terminal = (status?: string) => status === 'done' || status === 'error'

const STATO: Record<string, string> = {
  queued: 'In coda',
  running: 'In esecuzione',
  done: 'Completato',
  error: 'Errore',
}

type Level = JobLogLevel
type Entry = { time: string; level: Level; stage: string; body: string; deltaMs: number | null }

const COLORE: Record<Level, string> = {
  INFO: 'var(--theme-elevation-500)',
  WARN: 'var(--theme-warning-500, #b45309)',
  ERROR: 'var(--theme-error-500)',
}

/**
 * Dagli eventi grezzi alle righe da mostrare: ora LOCALE (il log è in UTC, e chi guarda no) e
 * tempo trascorso dalla fase precedente, che è il modo in cui si vede quale passo si è impantanato.
 */
function toEntries(raw: string | null | undefined): Entry[] {
  const parsed = parseJobLog(raw)
  return parsed.map((e, i) => ({
    time: e.at.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    level: e.level,
    stage: e.stage,
    body: e.body,
    deltaMs: i > 0 ? e.at.getTime() - parsed[i - 1].at.getTime() : null,
  }))
}

/** Le intestazioni del blocco di diagnosi, per poterle rendere in grassetto senza inventarle qui. */
const TESTATE = ['COSA È SUCCESSO', 'CHI PUÒ RISOLVERLO', 'COSA FARE ADESSO', 'DA RIFERIRE AI DEV', 'Rilievi:']

/**
 * La riga di sintesi con cui il flusso rimpiazza il blocco di diagnosi quando questo è già scritto
 * per intero nel riquadro in cima. Senza, la stessa mezza pagina di testo comparirebbe due volte
 * nella stessa schermata: la ripetizione non aggiunge niente e allontana il log dagli occhi.
 */
function sintesi(body: string): string {
  const righe = body.split('\n')
  const i = righe.indexOf('COSA È SUCCESSO')
  return (i >= 0 ? righe[i + 1] : righe[0]) || 'Errore'
}

function Corpo({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((riga, i) => {
        const testata = TESTATE.includes(riga.trim())
        return (
          <div
            key={i}
            style={{
              color: testata ? 'var(--theme-elevation-800)' : undefined,
              fontWeight: testata ? 700 : undefined,
              letterSpacing: testata ? '0.04em' : undefined,
              marginTop: testata && i > 0 ? 10 : undefined,
            }}
          >
            {riga || ' '}
          </div>
        )
      })}
    </>
  )
}

export default function JobLiveMonitor() {
  const { id } = useDocumentInfo()
  const [job, setJob] = useState<JobSnapshot | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [copiato, setCopiato] = useState(false)
  const logRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!id) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let controller: AbortController | undefined

    const poll = async () => {
      controller = new AbortController()
      try {
        const res = await fetch(`/api/generationJobs/${id}?depth=0`, {
          credentials: 'include',
          cache: 'no-store',
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const next = (await res.json()) as JobSnapshot
        if (stopped) return
        setJob(next)
        setFetchError(null)
        // Un job concluso non cambiera' piu': evita polling inutile finche' la pagina resta aperta.
        if (!terminal(next.status)) timer = setTimeout(poll, POLL_MS)
      } catch (error: unknown) {
        if (stopped || errName(error) === 'AbortError') return
        setFetchError(errMessage(error) || 'lettura non riuscita')
        timer = setTimeout(poll, POLL_MS)
      }
    }

    void poll()
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
      controller?.abort()
    }
  }, [id])

  const entries = useMemo(() => toEntries(job?.jobLog), [job?.jobLog])

  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [entries.length])

  if (!id) return null

  const percent = Math.max(0, Math.min(100, Number(job?.progress ?? 0)))
  const active = job?.status === 'queued' || job?.status === 'running'
  const diagnosi = job?.error?.trim() || ''
  // Un `error` valorizzato su un job ancora in coda non è un fallimento definitivo: è un tentativo
  // andato male con il retry già pianificato. Va detto, altrimenti l'autore legge «errore» e
  // rilancia a mano una cosa che stava già ripartendo da sola.
  const inErrore = job?.status === 'error'
  const ritenta = !!diagnosi && job?.status === 'queued'
  const codice = diagnosi.match(/codice:\s*(\S+)/)?.[1]

  const copia = async () => {
    const testo = [`Job ${id} — ${STATO[job?.status ?? ''] ?? job?.status ?? ''}`, '', diagnosi].join('\n')
    try {
      await navigator.clipboard.writeText(testo)
    } catch {
      // clipboard non disponibile (contesto non sicuro): si ripiega sulla selezione manuale
      const ta = document.createElement('textarea')
      ta.value = testo
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(ta)
      }
    }
    setCopiato(true)
    setTimeout(() => setCopiato(false), 2500)
  }

  return (
    <section style={{ border: '1px solid var(--theme-elevation-150)', borderRadius: 8, marginBottom: 24, overflow: 'hidden' }}>
      <div style={{ background: 'var(--theme-elevation-50)', padding: '16px 18px' }}>
        <div style={{ alignItems: 'baseline', display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <strong>Monitoraggio live</strong>
          <span style={{ color: fetchError ? 'var(--theme-error-500)' : 'var(--theme-elevation-600)', fontSize: 12 }}>
            {fetchError ? `Connessione: ${fetchError}` : active ? 'Aggiornamento ogni 2 secondi' : 'Aggiornamento concluso'}
          </span>
        </div>
        <div style={{ alignItems: 'center', display: 'flex', gap: 12, marginTop: 12 }}>
          <div
            aria-label={`Avanzamento ${percent}%`}
            style={{ background: 'var(--theme-elevation-150)', borderRadius: 999, flex: 1, height: 12, overflow: 'hidden' }}
          >
            <div
              style={{
                background: inErrore ? 'var(--theme-error-500)' : 'var(--theme-success-500)',
                height: '100%',
                transition: 'width 250ms ease',
                width: `${percent}%`,
              }}
            />
          </div>
          <strong style={{ minWidth: 42, textAlign: 'right' }}>{percent}%</strong>
        </div>
        <div style={{ marginTop: 8 }}>
          <strong>{STATO[job?.status ?? ''] ?? 'Caricamento'}</strong>
          {job?.progressStage ? <span style={{ color: 'var(--theme-elevation-600)' }}> · {job.progressStage}</span> : null}
          {job?.attempts ? <span style={{ color: 'var(--theme-elevation-600)' }}> · tentativo {job.attempts}</span> : null}
        </div>
      </div>

      {diagnosi ? (
        <div
          style={{
            background: inErrore ? 'var(--theme-error-50, #fef2f2)' : 'var(--theme-warning-50, #fffbeb)',
            borderTop: '1px solid var(--theme-elevation-150)',
            borderBottom: '1px solid var(--theme-elevation-150)',
            padding: '16px 18px',
          }}
        >
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
            <strong style={{ color: inErrore ? 'var(--theme-error-500)' : 'var(--theme-warning-500, #b45309)' }}>
              {inErrore ? "L'articolo non è stato generato" : 'Un tentativo non è riuscito — il sistema sta riprovando'}
              {codice ? ` · ${codice}` : ''}
            </strong>
            <button
              type="button"
              onClick={copia}
              style={{
                background: 'var(--theme-elevation-0)',
                border: '1px solid var(--theme-elevation-250)',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                padding: '6px 12px',
              }}
            >
              {copiato ? 'Copiato' : 'Copia per i dev'}
            </button>
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: 12,
              lineHeight: 1.65,
              marginTop: 12,
              whiteSpace: 'pre-wrap',
            }}
          >
            <Corpo text={diagnosi} />
          </div>
          {ritenta ? (
            <div style={{ color: 'var(--theme-elevation-600)', fontSize: 12, marginTop: 10 }}>
              Non serve rilanciare a mano: il job è già in coda per il tentativo successivo.
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        ref={logRef}
        style={{
          background: 'var(--theme-elevation-0)',
          fontSize: 12,
          lineHeight: 1.6,
          maxHeight: 360,
          minHeight: 120,
          overflow: 'auto',
          padding: 18,
        }}
      >
        {entries.length === 0 ? (
          <span style={{ color: 'var(--theme-elevation-500)' }}>In attesa del primo evento…</span>
        ) : (
          entries.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
              <span style={{ color: 'var(--theme-elevation-400)', fontFamily: 'var(--font-mono, monospace)', flexShrink: 0 }}>
                {e.time}
              </span>
              <span style={{ color: COLORE[e.level], flexShrink: 0, fontWeight: e.level === 'INFO' ? 400 : 700, minWidth: 128 }}>
                {e.stage}
              </span>
              <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                {diagnosi && e.body.trim() === diagnosi.trim() ? (
                  <>
                    <div>{sintesi(e.body)}</div>
                    <div style={{ color: 'var(--theme-elevation-400)' }}>Dettaglio completo nel riquadro qui sopra.</div>
                  </>
                ) : (
                  <Corpo text={e.body} />
                )}
              </span>
              {e.deltaMs != null && e.deltaMs >= 2000 ? (
                <span
                  title="tempo trascorso dalla fase precedente"
                  style={{ color: 'var(--theme-elevation-400)', flexShrink: 0, fontFamily: 'var(--font-mono, monospace)' }}
                >
                  +{humanDuration(e.deltaMs)}
                </span>
              ) : null}
            </div>
          ))
        )}
      </div>
    </section>
  )
}
